import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiErrorMessage } from "../services/apiErrors";
import { modelStatusCopy } from "../services/modelling/modelStatus.copy";
import { fetchModelStatus } from "../services/modelling/modelStatusApi";
import type { ModelStatusApi } from "../services/modelling/modelStatus.types";
import { queryKeys } from "../services/queryKeys";
import { useArchitecture } from "./useArchitecture";

/** How often the status is read again while a build runs or may start soon. */
export const MODEL_STATUS_POLL_MS = 10_000;

/** Building, data on its way to a build, or no data model yet (data may be loading). */
const POLLED = new Set<ModelStatusApi["state"]>(["building", "preparing", "none"]);

export interface UseModelStatusResult {
  /** Null on the old architecture, while loading, and when the status can't be read. */
  status: ModelStatusApi | null;
  /** The backend's reason the status couldn't be read; null otherwise. */
  errorMessage: string | null;
  retry: () => void;
}

/**
 * Where the tenant's data model stands, for a tenant on the new architecture only. Read again
 * until a data model exists and while a build runs or is on its way; when a new release is served,
 * the knowledge-graph pages read theirs again.
 */
export const useModelStatus = (): UseModelStatusResult => {
  const architecture = useArchitecture();
  const enabled = architecture.status === "ready" && !architecture.oldArchitecture;
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.modelling.status,
    queryFn: fetchModelStatus,
    enabled,
    retry: false,
    refetchInterval: (current) =>
      current.state.data && POLLED.has(current.state.data.state) ? MODEL_STATUS_POLL_MS : false,
  });

  const releaseId = query.data ? (query.data.release?.id ?? null) : undefined;
  const seenReleaseId = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (releaseId === undefined) return;
    if (seenReleaseId.current !== undefined && releaseId && releaseId !== seenReleaseId.current) {
      void queryClient.invalidateQueries({ queryKey: ["kg"] });
    }
    seenReleaseId.current = releaseId;
  }, [queryClient, releaseId]);

  return {
    status: enabled ? (query.data ?? null) : null,
    errorMessage: enabled && query.isError ? getApiErrorMessage(query.error, modelStatusCopy.unreadable) : null,
    retry: () => void query.refetch(),
  };
};
